<script setup lang="ts">
/**
 * Read-only list of a patron's tournament finishes, newest first.
 *
 * @changelog
 *
 * 2026-09-08 (0.1.0) (jhoward)
 *   - Add App\components\TournamentResults.vue to render the tournament_results the account info endpoint returns
 *     alongside account info.
 *
 * 2026-09-09 (0.2.0) (jhoward)
 *   - Change the date column to the stacked treatment src\helpers\getReadableDate.ts describes: the date as a person
 *     would write it over how long ago it was, in a <time> element, replacing the bare 'Sep 6, 2026'.
 *   - Restate the table against the Material Design data table metrics - a 56px header of 12px medium labels, 52px
 *     rows of 14px text, 16px column padding and 1px dividers at 0.12 - and drop the 2px --primary-color rule under
 *     the header for Material's neutral divider.
 *   - Give the property and player columns medium emphasis and the score weight 500, so the eye lands on the
 *     tournament and its score rather than reading all five columns flat.
 *   - Replace the bare empty-state sentence with a centred icon and message.
 *
 * 2026-09-22 (0.3.0) (jhoward)
 *   - Add the prize disclaimers under the table: the free play expiry for every property, and the 48 hour cash prize
 *     claim window whenever a Western Village result is listed.
 *
 * 2026-09-23 (0.4.0) (jhoward)
 *   - Add rank and prize columns, and reorder to date, tournament, property, rank, prize, score. The player column
 *     keeps its place after the tournament whenever results span linked accounts.
 *   - A result with no rank or prize shows a dash rather than an empty cell. A rank of 0 is no rank, so it gets the
 *     dash too.
 *
 * 2026-10-09 (0.4.1) (jhoward)
 *   - Mark the empty state, table scroller and disclaimers layout--content-wide. Inside a form container every child of
 *     a content block is given three of the six grid columns, so the table was drawing at half width.
 *
 * 2026-10-09 (0.5.0) (jhoward)
 *   - Draw each result as a list item whenever the table does not fit its card, instead of scrolling it sideways: the
 *     tournament and its score on the title line, then date, property, rank and prize as labelled lines (from each
 *     cell's data-label). The score used to sit off the right edge of a phone. It is measured, not a breakpoint - the
 *     table needs about 700px, so a fixed 600px left it scrolling up to roughly 800px, and the player column that linked
 *     accounts add needs more.
 *   - Set the compact lines as a fixed label column with the value beside it, rather than label at one edge and value at
 *     the other. In a card up to about 800px wide that left a wide gap between a label and the data it names.
 *   - Draw the score as the first labelled line under the tournament name, not at the far edge of the title line, where
 *     it sat most of a card's width away from the name it belongs to.
 *   - Give the table elements explicit ARIA roles. The compact layout changes their display, which makes a browser drop
 *     their table semantics; the roles keep it one table for a screen reader. The header row is hidden, not removed.
 */

import PeppermillContentBlock from '@framework/components/layout/PeppermillContentBlock.vue';
import type { PropType } from 'vue';
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue';
import type { OpcTournamentResult } from '@/defs/api/TournamentResult';
import { getDateFromEngage } from '@/helpers/getdateFromEngage';
import getPropertyName from '@/helpers/getPropertyName';
import getReadableDate from '@/helpers/getReadableDate';
import type { ReadableDate } from '@/helpers/getReadableDate';

const props = defineProps({
  results: {
    type: Array as PropType<OpcTournamentResult[]>,
    default: () => [],
  },
});

//Newest first. Anything the Engage date regex cannot parse sorts to the bottom rather than to 1970.
const sortedResults = computed<OpcTournamentResult[]>(() => {
  return [...props.results].sort((a, b) => {
    const dateA = getDateFromEngage(a.TOURNAMENT_DATE)?.getTime() ?? -Infinity;
    const dateB = getDateFromEngage(b.TOURNAMENT_DATE)?.getTime() ?? -Infinity;

    return dateB - dateA;
  });
});

//Formatted up front rather than in the template, so each row's date is read and phrased once instead of once per line.
const readableDates = computed<ReadableDate[]>(() => {
  return sortedResults.value.map((result) => getReadableDate(result.TOURNAMENT_DATE));
});

//The cash prize claim window is a Western Village rule, so it is stated only when a WV result is listed.
const hasWesternVillageResult = computed<boolean>(() => {
  return props.results.some((result) => '2B' === result.TOURNAMENT_PROPERTY_CODE);
});

//Results can span linked accounts, so the patron column earns its space only when there is more than one of them.
const hasMultiplePatrons = computed<boolean>(() => {
  return new Set(props.results.map((result) => result.CASINO_PATRON_ID)).size > 1;
});

//Whether the results are drawn as list items rather than as a table. How wide the table needs to be depends on its data
//(a long prize name, the player column), so there is no breakpoint to name: it is compact exactly when the table does
//not fit its card, and goes back to a table once the card is as wide as the table was when it overflowed.
const scroller = useTemplateRef<HTMLDivElement>('scroller');
const isCompact = ref<boolean>(false);
let neededWidth = 0;
let resizeObserver: ResizeObserver | null = null;

function updateLayout() {
  const element = scroller.value;
  if (!element) {
    return;
  }

  if (!isCompact.value) {
    if (element.scrollWidth > element.clientWidth) {
      neededWidth = element.scrollWidth;
      isCompact.value = true;
    }
  } else if (element.clientWidth >= neededWidth) {
    isCompact.value = false;
  }
}

//The scroller only exists once there are results, so the observer follows it rather than starting at mount.
watch(
  scroller,
  (element) => {
    resizeObserver?.disconnect();
    if (!element) {
      return;
    }
    resizeObserver ??= new ResizeObserver(updateLayout);
    resizeObserver.observe(element);
    //The table's own width changes without the card's, as web fonts arrive.
    const table = element.querySelector('table');
    if (table) {
      resizeObserver.observe(table);
    }
    nextTick(updateLayout);
  },
  { flush: 'post' },
);

//New data is a new width: forget what the last set needed and measure again.
watch(sortedResults, () => {
  neededWidth = 0;
  isCompact.value = false;
  nextTick(updateLayout);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});
</script>

<template>
  <peppermill-content-block>
    <div v-if="!sortedResults.length" class="tournament_results--empty layout--content-wide">
      <span class="tournament_results--empty_icon material-icons" aria-hidden="true">emoji_events</span>
      <p class="tournament_results--empty_message">We don't have any tournament results for your account.</p>
    </div>

    <!--A real table, kept scrollable rather than restyled into blocks, so the row/column relationships survive for
        anyone reading this with assistive tech.-->
    <template v-else>
      <div ref="scroller" class="tournament_results--scroller layout--content-wide" :class="{ 'is-compact': isCompact }">
        <table class="tournament_results--table" role="table">
          <caption class="tournament_results--caption">
            Your tournament results, most recent first.
          </caption>
          <thead role="rowgroup">
            <tr role="row">
              <th scope="col" role="columnheader">Date</th>
              <th scope="col" role="columnheader">Tournament</th>
              <th v-if="hasMultiplePatrons" scope="col" role="columnheader">Player</th>
              <th scope="col" role="columnheader">Property</th>
              <th scope="col" role="columnheader" class="tournament_results--numeric">Rank</th>
              <th scope="col" role="columnheader">Prize</th>
              <th scope="col" role="columnheader" class="tournament_results--numeric tournament_results--score">Score</th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            <tr
              role="row"
              v-for="(result, index) in sortedResults"
              :key="
                result.CASINO_PATRON_ID + '::' + result.TOURNAMENT_DATE + '::' + result.TOURNAMENT_NAME + '::' + index
              "
            >
              <td class="tournament_results--date" role="cell" data-label="Date">
                <!--One <time> around both lines: they are two readings of the same instant, not two facts.-->
                <time :datetime="readableDates[index].machine || undefined">
                  <span class="tournament_results--date_absolute">{{ readableDates[index].absolute }}</span>
                  <span v-if="readableDates[index].relative" class="tournament_results--date_relative">
                    {{ readableDates[index].relative }}
                  </span>
                </time>
              </td>
              <td class="tournament_results--name" role="cell" data-label="Tournament">{{ result.TOURNAMENT_NAME }}</td>
              <td v-if="hasMultiplePatrons" class="tournament_results--secondary" role="cell" data-label="Player">
                {{ result.CASINO_PATRON_FULL_NAME }}
              </td>
              <td class="tournament_results--secondary" role="cell" data-label="Property">{{ getPropertyName(result.TOURNAMENT_PROPERTY_CODE) }}</td>
              <td class="tournament_results--numeric" role="cell" data-label="Rank">
                {{ result.TOURNAMENT_RESULT_RANK ? result.TOURNAMENT_RESULT_RANK.toLocaleString() : '—' }}
              </td>
              <td role="cell" data-label="Prize">{{ result.TOURNAMENT_RESULT_PRIZE || '—' }}</td>
              <td class="tournament_results--numeric tournament_results--score" role="cell" data-label="Score">
                {{ result.TOURNAMENT_RESULT_SCORE.toLocaleString() }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="tournament_results--disclaimers layout--content-wide">
        <p>*Free Play Valid for 30 Days from Issued date</p>
        <p v-if="hasWesternVillageResult">Cash Prizes must be claimed within 48 hours of winning.</p>
      </div>
    </template>
  </peppermill-content-block>
</template>

<style scoped>
/*The metrics below are the Material Design data table specs in px: a 56px header row of 12px medium labels, 52px
  data rows of 14px text, 16px of column padding, and 1px dividers at the 0.12 alpha Material uses for them. px
  rather than em, the same way <peppermill-badge> is specified, so the table keeps its shape wherever it lands.*/

/*Contains the overflow here rather than letting a long tournament name push the page sideways.*/
.tournament_results--scroller {
  overflow-x: auto;
}

.tournament_results--table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}

.tournament_results--caption {
  margin-bottom: 16px;
  text-align: left;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.6);
}

.tournament_results--table th,
.tournament_results--table td {
  padding: 0 16px;
  text-align: left;
  white-space: nowrap;
}

/*The content block already pads the card by 24px, so the outer columns give up their own edge padding and line up
  with the caption and the heading above it instead of sitting 16px inside them.*/
.tournament_results--table th:first-child,
.tournament_results--table td:first-child {
  padding-left: 0;
}

.tournament_results--table th:last-child,
.tournament_results--table td:last-child {
  padding-right: 0;
}

.tournament_results--table th {
  height: 56px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  letter-spacing: 0.0333em;
  color: rgba(0, 0, 0, 0.6);
}

/*16px of padding around a 20px line is Material's 52px data row, without pinning a height - so a row grows for the
  stacked date, or a tournament name that wraps, instead of overflowing it.

  Top-aligned rather than centred: the date runs to two lines and the columns beside it to one, and centring each cell
  in the row separately left the date sitting a few px above the tournament name it belongs to. Material top-aligns
  the primary line of a two-line row for the same reason.*/
.tournament_results--table td {
  padding-top: 16px;
  padding-bottom: 16px;
  vertical-align: top;
  line-height: 20px;
}

.tournament_results--table tbody tr + tr td {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

/*Material's 0.04 hover overlay. Nothing here is clickable; it is a reading aid for tracking one row across a table
  wide enough to scroll. Gated on a real pointer so it does not stick to whatever was last tapped.*/
@media (hover: hover) {
  .tournament_results--table tbody tr:hover td {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

.tournament_results--date time {
  display: flex;
  flex-direction: column;
}

.tournament_results--date_relative {
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.6);
}

/*The tournament name is the one column worth letting wrap on a narrow screen; the rest stay on one line.*/
.tournament_results--table td.tournament_results--name {
  white-space: normal;
  min-width: 12em;
}

/*Which property, and which linked patron, are how you tell two similar rows apart rather than what you came to read,
  so they sit at Material's medium emphasis and leave the tournament and its score at full strength.*/
.tournament_results--secondary {
  color: rgba(0, 0, 0, 0.6);
}

/*Material right-aligns numeric columns.*/
.tournament_results--table th.tournament_results--numeric,
.tournament_results--table td.tournament_results--numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/*The extra weight is not in the Material spec - the score is what the row is read for.*/
.tournament_results--table td.tournament_results--score {
  font-weight: 500;
}

/*Caption-sized secondary text, so the fine print reads as belonging to the table without competing with it.*/
.tournament_results--disclaimers {
  margin-top: 16px;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.6);
}

.tournament_results--disclaimers p {
  margin: 0;
}

.tournament_results--disclaimers p + p {
  margin-top: 4px;
}

.tournament_results--empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 0;
  text-align: center;
}

.tournament_results--empty_icon {
  font-size: 48px;
  color: rgba(0, 0, 0, 0.24);
}

.tournament_results--empty_message {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.6);
}

/*COMPACT LAYOUT. Seven columns do not fit a phone, and scrolling them sideways hides the score - the thing the row is
  read for - off the edge. So when the table does not fit its card (the script below measures that, because how wide the
  table needs to be depends on its data: a long prize name, or the player column that linked accounts add) each result
  stops looking like a table row and is drawn as a list item instead: the tournament and its score on the title line,
  then the date, property, rank and prize as labelled lines beneath it, with the labels read from each cell's
  data-label.

  The roles on the table elements are what keep it a table for assistive tech: a browser drops the table semantics of an
  element whose display is changed away from table, and an explicit role puts them back. The header row is hidden
  visually, not removed, so each cell is still announced against its column.*/
.tournament_results--scroller.is-compact {
  overflow-x: visible;
}

.is-compact .tournament_results--table,
.is-compact .tournament_results--table tbody {
  display: block;
}

/*A caption stays display: table-caption, which inside a table that is now a block shrinks to its own words.*/
.is-compact .tournament_results--caption {
  display: block;
}

.is-compact .tournament_results--table thead {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.is-compact .tournament_results--table tbody tr {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 16px;
  padding: 16px 0;
}

.is-compact .tournament_results--table tbody tr + tr {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

/*The divider is the row's now, not each cell's.*/
.is-compact .tournament_results--table tbody tr + tr td {
  border-top: 0;
}

/*A fixed label column with the value beside it, so a label and its value read as a pair. Pushing the value to the far
  edge (space-between) left a wide gap between them whenever the card was wide - up to about 800px.*/
.is-compact .tournament_results--table tbody td {
  display: grid;
  grid-template-columns: 6em minmax(0, 1fr);
  grid-column: 1 / -1;
  align-items: baseline;
  gap: 16px;
  min-width: 0;
  padding: 2px 0;
  white-space: normal;
  text-align: left;
}

.is-compact .tournament_results--table tbody td::before {
  content: attr(data-label);
  font-size: 12px;
  line-height: 20px;
  font-weight: 500;
  letter-spacing: 0.0333em;
  color: rgba(0, 0, 0, 0.6);
}

/*The title line is the tournament's name, with no label. The score is the first labelled line beneath it - next to the name
  it belongs to, in the same column as the other values, rather than at the far edge of the card - and set in medium
  weight, as it is what the row is read for.*/
.is-compact .tournament_results--table tbody td.tournament_results--name {
  display: block;
  grid-column: 1 / -1;
  grid-row: 1;
  min-width: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  text-align: left;
}

.is-compact .tournament_results--table tbody td.tournament_results--name::before {
  content: none;
}

/*Auto-placed lines fill in DOM order, which has the score last; order puts it first, directly under the name.*/
.is-compact .tournament_results--table tbody td.tournament_results--score {
  order: -1;
}

/*Absolute and relative date on one line, rather than stacked.*/
.is-compact .tournament_results--date time {
  flex-direction: row;
  align-items: baseline;
  gap: 8px;
}
</style>
